// ---------------------------------------------------------------------------
// Metric card — large number display with trend indicator
// ---------------------------------------------------------------------------

import React from 'react';

interface MetricCardProps {
  /** Main label displayed above the value. */
  title: string;
  /** The metric value to display prominently. */
  value: string | number;
  /** Optional subtitle below the value. */
  subtitle?: string;
  /** Trend direction indicator. */
  trend?: 'up' | 'down' | 'flat';
  /** Percentage change for the trend. */
  trendValue?: number;
  /** SVG icon element displayed in the top-right corner. */
  icon?: React.ReactNode;
}

const TREND_STYLES = {
  up: { color: 'text-primary', bg: 'bg-primary-tint', arrow: '\u2191' },
  down: { color: 'text-danger', bg: 'bg-danger-tint', arrow: '\u2193' },
  flat: { color: 'text-ink-muted', bg: 'bg-surface-sunken', arrow: '\u2192' },
} as const;

export function MetricCard({
  title,
  value,
  subtitle,
  trend,
  trendValue,
  icon,
}: MetricCardProps) {
  const trendStyle = trend ? TREND_STYLES[trend] : null;

  return (
    <div className="bg-surface-raised rounded-sm border border-hairline p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-ink-muted truncate">{title}</p>
          <p className="mt-2 font-mono text-3xl font-medium text-ink tabular-nums">{value}</p>
          {subtitle && (
            <p className="mt-1 text-sm text-ink-muted">{subtitle}</p>
          )}
        </div>
        {icon && (
          <div className="ms-4 flex-shrink-0 w-10 h-10 rounded-sm bg-primary-tint flex items-center justify-center text-primary">
            {icon}
          </div>
        )}
      </div>

      {trendStyle && trendValue !== undefined && (
        <div className="mt-3 flex items-center gap-1.5">
          <span
            className={`inline-flex items-center rounded-sm px-2 py-0.5 font-mono text-[0.6875rem] font-medium tabular-nums ${trendStyle.bg} ${trendStyle.color}`}
          >
            {trendStyle.arrow} {Math.abs(trendValue).toFixed(1)}%
          </span>
          <span className="text-xs text-ink-muted">vs previous period</span>
        </div>
      )}
    </div>
  );
}
